// src/features/task/form/TemplateMenu.tsx
// "Start from template" — a quiet menu beside the page heading, not a band.
//
// It exists only when this scope actually offers templates, so a scope with
// none never spends a pixel saying so. Two projects may each own a template
// called "Bug report", which is why every entry keeps its origin line.

import { useI18n } from '@react/hooks';
import type { TaskTemplate } from '@core/types/task';
import { FORM_ICONS } from './icons';
import { PropertyPill, PillOptionList } from './PropertyPill';
import type { PillOption } from './PropertyPill';
import { templateOriginLabelKey } from './scope';
import type { TemplateEntry } from './scope';

export interface TemplateMenuProps {
  entries: TemplateEntry[];
  /** Selected template id, or null for a blank task. */
  value: string | null;
  onSelect: (template: TaskTemplate | null) => void;
  /** How the current parent reads in prose — used in origin labels. */
  parentLabel: string;
  /** Link to the template authoring surface, rendered at the menu's foot. */
  createHref: string;
}

export function TemplateMenu({
  entries,
  value,
  onSelect,
  parentLabel,
  createHref,
}: TemplateMenuProps) {
  const { t } = useI18n();
  if (entries.length === 0) return null;

  const selected = entries.find((entry) => entry.id === value) ?? null;

  const options: PillOption[] = [
    { value: '', label: t('task.createForm.blankTask'), icon: FORM_ICONS.blank },
    ...entries.map((entry) => ({
      value: entry.id,
      label: entry.name,
      icon: FORM_ICONS.template,
      hint:
        t(templateOriginLabelKey(entry.origin), {
          parent: parentLabel,
          type: entry.origin.scopeType || t('task.createForm.parent.tenant'),
        }) + (entry.disambiguator ? ` · ${entry.disambiguator}` : ''),
    })),
  ];

  return (
    <PropertyPill
      icon={FORM_ICONS.template}
      label={t('task.createForm.startFromTemplate')}
      value={selected?.name}
      variant="quiet"
      popoverTitle={t('task.createForm.startFromTemplate')}
      popoverWidth={320}
    >
      {(close) => (
        <>
          <PillOptionList
            options={options}
            value={value ?? ''}
            ariaLabel={t('task.createForm.startFromTemplate')}
            onSelect={(id) => {
              const entry = entries.find((candidate) => candidate.id === id);
              onSelect(entry ? entry.template : null);
              close();
            }}
          />
          {selected && (
            <p className="border-t border-border pt-2 text-xs text-muted-foreground">
              {t('task.createFromTemplateNote')}
            </p>
          )}
          <a
            href={createHref}
            target="_blank"
            rel="noreferrer"
            className="rounded-sm text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('task.createTemplate')}
          </a>
        </>
      )}
    </PropertyPill>
  );
}
